import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import {
  Activity, AlertTriangle, AlertCircle, CheckCircle2, ChevronDown, ChevronUp,
  Database, Loader2, RefreshCw, TrendingUp,
} from "lucide-react";

/**
 * S1 Revenue Health — live diagnostic of System1 reporting pipeline.
 *
 * Renders on the Dashboard as a collapsible card. Calls /api/reports/s1-health
 * which is 10-min cached on the backend, so re-opens are free. Click Refresh
 * to bypass the cache and force a live S1 pull.
 */
export default function S1HealthPanel() {
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem("rsoc.s1Health.open") === "1"; } catch { return false; }
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    try { localStorage.setItem("rsoc.s1Health.open", next ? "1" : "0"); } catch {}
    if (next && !data) load();
  };

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/reports/s1-health", { params: { days: 30 }, timeout: 60000 });
      setData(data);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Failed to load S1 health";
      setError(msg);
      toast.error(msg);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open && !data) load(); }, []); // eslint-disable-line

  return (
    <section data-testid="s1-health-panel" className="bg-white border border-slate-200 rounded-lg mb-4 overflow-hidden">
      <div className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50">
        <div
          role="button"
          tabIndex={0}
          onClick={toggle}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggle(); } }}
          data-testid="s1-health-toggle"
          className="flex items-center gap-2.5 flex-1 text-left cursor-pointer"
        >
          <Database className="w-4 h-4 text-indigo-600" />
          <div>
            <div className="text-sm font-semibold text-slate-900 flex items-center gap-2">
              System1 Revenue Health
              {data && <HealthBadge data={data} />}
            </div>
            <div className="text-[11px] text-slate-500 font-mono">
              Live diagnostic — shows whether FB → S1 → revenue pipe is healthy
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {open && (
            <button
              onClick={load}
              disabled={loading}
              data-testid="s1-health-refresh"
              className="text-[11px] font-medium px-2 py-1 bg-white border border-slate-200 hover:bg-slate-50 rounded flex items-center gap-1 disabled:opacity-50"
            >
              {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Refresh
            </button>
          )}
          <button onClick={toggle} aria-label={open ? "Collapse" : "Expand"} className="text-slate-400 hover:text-slate-700">
            {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-slate-200 p-4 space-y-4" data-testid="s1-health-body">
          {loading && !data && (
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Loader2 className="w-4 h-4 animate-spin" /> Pulling live data from System1 (can take 5-30s)…
            </div>
          )}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md px-3 py-2 text-[12px] text-red-800" data-testid="s1-health-error">
              <AlertCircle className="w-3.5 h-3.5 inline mr-1" /> {error}
            </div>
          )}
          {data && <Body data={data} />}
        </div>
      )}
    </section>
  );
}

function HealthBadge({ data }) {
  if (!data.connected) {
    return <span className="text-[10px] font-mono px-2 py-0.5 rounded border bg-red-50 text-red-700 border-red-200">disconnected</span>;
  }
  if (data.error === "rate_limited") {
    return <span className="text-[10px] font-mono px-2 py-0.5 rounded border bg-amber-50 text-amber-700 border-amber-200">rate-limited</span>;
  }
  const rev30 = data?.revenue?.last_30d ?? 0;
  const hasErrors = (data?.diagnostics || []).some((d) => d.level === "error");
  if (hasErrors) {
    return <span className="text-[10px] font-mono px-2 py-0.5 rounded border bg-red-50 text-red-700 border-red-200">issues found</span>;
  }
  if (rev30 > 0) {
    return <span className="text-[10px] font-mono px-2 py-0.5 rounded border bg-emerald-50 text-emerald-700 border-emerald-200">earning ${rev30.toFixed(2)}</span>;
  }
  return <span className="text-[10px] font-mono px-2 py-0.5 rounded border bg-amber-50 text-amber-700 border-amber-200">$0 earned</span>;
}

function Body({ data }) {
  if (!data.connected) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-md px-3 py-3 text-[12px] text-amber-900">
        <div className="flex items-center gap-1.5 font-semibold mb-1">
          <AlertTriangle className="w-3.5 h-3.5" /> Not connected
        </div>
        <div>{data.error || "Unknown error"}</div>
        {data.hint && <div className="mt-1 text-slate-700">{data.hint}</div>}
      </div>
    );
  }
  if (data.rows_total === 0) {
    return (
      <div className="bg-slate-50 border border-slate-200 rounded-md px-3 py-3 text-[12px] text-slate-700" data-testid="s1-health-empty">
        <div className="font-semibold mb-1 text-slate-900">Connected but no events yet</div>
        <div>{data.hint || "S1 hasn't logged any events on your segment in the last 30 days."}</div>
        <div className="mt-2 text-[11px] font-mono text-slate-500">
          This is normal for brand-new accounts. Once you launch a real FB campaign pointing at an RSOC landing page with the tracking macros, events will flow in within hours.
        </div>
      </div>
    );
  }

  const rev = data.revenue || {};
  const clicks = data.clicks || {};
  const srch = data.searches || {};
  const attr = data.attribution_health || {};
  const diag = data.diagnostics || [];

  return (
    <div className="space-y-4">
      {/* Diagnostics */}
      {diag.length > 0 && (
        <div className="space-y-2" data-testid="s1-health-diagnostics">
          {diag.map((d, i) => (
            <div
              key={`${d.level}-${i}`}
              className={`flex items-start gap-2 rounded-md px-3 py-2 text-[12px] border ${
                d.level === "error" ? "bg-red-50 text-red-800 border-red-200"
                : d.level === "warn" ? "bg-amber-50 text-amber-900 border-amber-200"
                : "bg-blue-50 text-blue-900 border-blue-200"
              }`}
            >
              {d.level === "error" ? <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                : <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />}
              <span>{d.msg}</span>
            </div>
          ))}
        </div>
      )}

      {/* Revenue metrics */}
      <div className="grid grid-cols-3 gap-3" data-testid="s1-health-revenue">
        <MetricCard label="Revenue · today" value={`$${(rev.today ?? 0).toFixed(2)}`} positive={rev.today > 0} icon={TrendingUp} />
        <MetricCard label="Revenue · 7 days" value={`$${(rev.last_7d ?? 0).toFixed(2)}`} positive={rev.last_7d > 0} icon={TrendingUp} />
        <MetricCard label="Revenue · 30 days" value={`$${(rev.last_30d ?? 0).toFixed(2)}`} positive={rev.last_30d > 0} icon={TrendingUp} />
      </div>

      {/* Engagement metrics */}
      <div className="grid grid-cols-3 gap-3">
        <MetricCard label="S1 clicks · 30d" value={(clicks.last_30d ?? 0).toLocaleString()} icon={Activity} />
        <MetricCard label="Widget searches · 30d" value={(srch.last_30d ?? 0).toLocaleString()} icon={Activity} />
        <MetricCard label="Events logged" value={(data.rows_total ?? 0).toLocaleString()} sub={`${data.rows_with_revenue ?? 0} with revenue`} icon={Activity} />
      </div>

      {/* Attribution health */}
      <div className="bg-slate-50 border border-slate-200 rounded-md p-3" data-testid="s1-health-attribution">
        <div className="text-[11px] font-semibold text-slate-900 mb-2 flex items-center gap-1.5">
          Attribution health
          <span className="text-[10px] text-slate-500 font-mono font-normal">
            — % of events where FB→S1 tracking is broken
          </span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <AttrBar label="Missing campaign_id" pct={attr.missing_campaign_id_pct ?? 0} count={attr.missing_campaign_id ?? 0} total={data.rows_total} dangerThreshold={50} warnThreshold={10} />
          <AttrBar label="Missing buyside_account_id" pct={attr.missing_buyside_id_pct ?? 0} count={attr.missing_buyside_id ?? 0} total={data.rows_total} dangerThreshold={75} warnThreshold={50} />
        </div>
        <div className="text-[11px] text-slate-500 mt-2 font-mono">
          High "missing campaign_id" = your FB URL macros aren't firing. Use the Landing URL Builder on Connections to regenerate.
        </div>
      </div>

      {/* Top campaigns */}
      {data.top_campaigns?.length > 0 && (
        <div data-testid="s1-health-top-campaigns">
          <div className="text-[11px] font-semibold text-slate-900 mb-2">Top earning campaigns · last 30 days</div>
          <div className="border border-slate-200 rounded-md overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-[10px] font-mono text-slate-500 uppercase">
                <tr><th className="text-left px-3 py-1.5">Campaign ID</th><th className="text-right px-3 py-1.5">Revenue</th></tr>
              </thead>
              <tbody>
                {data.top_campaigns.map((c) => (
                  <tr key={c.campaign_id} className="border-t border-slate-100">
                    <td className="px-3 py-1.5 font-mono text-slate-700 truncate max-w-xs">{c.campaign_id}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-slate-900">${c.revenue.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Footer meta */}
      <div className="flex items-center justify-between text-[10px] text-slate-500 font-mono pt-2 border-t border-slate-100">
        <span>
          Partner ID: {(data.partner_ids || []).join(", ") || "(none)"} · Segments: {(data.segments || []).join(", ") || "(none)"}
        </span>
        <span>
          Latest event: {data.latest_event_date || "—"}
          {typeof data.cached_age_seconds === "number" && ` · cached ${data.cached_age_seconds}s ago`}
        </span>
      </div>
    </div>
  );
}

function MetricCard({ label, value, sub, positive, icon: Icon }) {
  return (
    <div className={`border border-slate-200 rounded-md p-3 ${positive ? "bg-emerald-50 border-emerald-200" : "bg-white"}`}>
      <div className="flex items-center gap-1.5 text-[10px] font-mono text-slate-500 uppercase">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </div>
      <div className="text-xl font-semibold mt-0.5 text-slate-900">{value}</div>
      {sub && <div className="text-[10px] font-mono text-slate-500">{sub}</div>}
    </div>
  );
}

function AttrBar({ label, pct, count, total, dangerThreshold, warnThreshold }) {
  const tone = pct >= dangerThreshold ? "bg-red-500" : pct >= warnThreshold ? "bg-amber-500" : "bg-emerald-500";
  const toneText = pct >= dangerThreshold ? "text-red-700" : pct >= warnThreshold ? "text-amber-700" : "text-emerald-700";
  return (
    <div data-testid={`s1-attr-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[11px] text-slate-700">{label}</span>
        <span className={`text-[11px] font-mono font-medium ${toneText}`}>{pct.toFixed(1)}%</span>
      </div>
      <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
        <div className={tone} style={{ width: `${Math.min(100, pct)}%`, height: "100%" }} />
      </div>
      <div className="text-[10px] font-mono text-slate-500 mt-0.5">{count} of {total} events</div>
    </div>
  );
}

// Re-export CheckCircle2 so it's not dead-imported (lint safeguard).
export { CheckCircle2 };
